/**
 * VisionAccount gRPC adapter for per-account appearance preferences.
 *
 * Presents the appearance store's wire shape (camelCase keys, lowercase enum strings) on
 * top of the `protos.vision.account.v1.VisionAccount` gRPC-web service, so the store is
 * unaware of the transport.
 */

import { createClient } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { VisionAccount } from '../gen/vision/account/v1/account_service_pb';
import { boardToProto, prefsFromProto } from './visionPreferencesMapping';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const BASE_URL = config.VISION_GRPC_BASE_URL;

const APPEARANCE_FIELD = {
  artboard: 'appearanceArtboard',
  storyboard: 'appearanceStoryboard',
  visionboard: 'appearanceVisionboard',
};

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  return next(req);
};

let cachedClient = null;

function client() {
  if (!cachedClient) {
    const transport = createGrpcWebTransport({
      baseUrl: BASE_URL,
      interceptors: [authInterceptor],
    });
    cachedClient = createClient(VisionAccount, transport);
  }
  return cachedClient;
}

export async function fetchVisionPreferences() {
  const response = await client().getMyVisionPreferences({ params: {} });
  const preferences = response.result?.preferences;
  return preferences ? prefsFromProto(preferences) : {};
}

export async function saveVisionBoard(sliceName, board) {
  const field = APPEARANCE_FIELD[sliceName];
  if (!field) {
    throw new Error(`Unknown appearance slice: ${sliceName}`);
  }
  await client().updateMyVisionPreferences({
    params: { [field]: boardToProto(board) },
    updateMask: { paths: [`params.appearance_${sliceName}`] },
  });
}
